// IndexedDB utilities for offline data storage

const DB_NAME = 'DineProOfflineDB';
const DB_VERSION = 2; // Incrementar versión para agregar nuevo store

// Store names
const STORES = {
  PRODUCTS: 'products',
  CATEGORIES: 'categories',
  TABLES: 'tables',
  ORDERS: 'orders',
  OFFLINE_QUEUE: 'offlineQueue',
  SETTINGS: 'settings',
  IMAGES: 'images' // Nuevo store para imágenes
};

let db = null;

// Initialize IndexedDB
export const initDB = () => {
  return new Promise((resolve, reject) => {
    if (db) {
      resolve(db);
      return;
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      console.error('[OfflineDB] Error opening database:', request.error);
      reject(request.error);
    };

    request.onsuccess = () => {
      db = request.result;
      console.info('[OfflineDB] Database opened successfully');
      resolve(db);
    };

    request.onupgradeneeded = (event) => {
      const database = event.target.result;
      console.info('[OfflineDB] Upgrading database...');

      // Products store
      if (!database.objectStoreNames.contains(STORES.PRODUCTS)) {
        const productsStore = database.createObjectStore(STORES.PRODUCTS, { keyPath: 'id' });
        productsStore.createIndex('category_id', 'category_id', { unique: false });
        productsStore.createIndex('tenant_id', 'tenant_id', { unique: false });
      }

      // Categories store
      if (!database.objectStoreNames.contains(STORES.CATEGORIES)) {
        const categoriesStore = database.createObjectStore(STORES.CATEGORIES, { keyPath: 'id' });
        categoriesStore.createIndex('tenant_id', 'tenant_id', { unique: false });
      }

      // Tables store
      if (!database.objectStoreNames.contains(STORES.TABLES)) {
        const tablesStore = database.createObjectStore(STORES.TABLES, { keyPath: 'id' });
        tablesStore.createIndex('tenant_id', 'tenant_id', { unique: false });
      }
      
      // Images store for offline image caching
      if (!database.objectStoreNames.contains(STORES.IMAGES)) {
        const imagesStore = database.createObjectStore(STORES.IMAGES, { keyPath: 'url' });
        imagesStore.createIndex('type', 'type', { unique: false }); // product, ingredient, category
        imagesStore.createIndex('cached_at', 'cached_at', { unique: false });
      }

      // Orders store (for offline created orders)
      if (!database.objectStoreNames.contains(STORES.ORDERS)) {
        const ordersStore = database.createObjectStore(STORES.ORDERS, { keyPath: 'id' });
        ordersStore.createIndex('status', 'status', { unique: false });
        ordersStore.createIndex('synced', 'synced', { unique: false });
      }

      // Offline queue for actions
      if (!database.objectStoreNames.contains(STORES.OFFLINE_QUEUE)) {
        const queueStore = database.createObjectStore(STORES.OFFLINE_QUEUE, { keyPath: 'id', autoIncrement: true });
        queueStore.createIndex('type', 'type', { unique: false });
        queueStore.createIndex('timestamp', 'timestamp', { unique: false });
      }

      // Settings store
      if (!database.objectStoreNames.contains(STORES.SETTINGS)) {
        database.createObjectStore(STORES.SETTINGS, { keyPath: 'key' });
      }
    };
  });
};

// Generic store operations
const getStore = async (storeName, mode = 'readonly') => {
  const database = await initDB();
  const transaction = database.transaction(storeName, mode);
  return transaction.objectStore(storeName);
};

// Save item to store
export const saveItem = async (storeName, item) => {
  const store = await getStore(storeName, 'readwrite');
  return new Promise((resolve, reject) => {
    const request = store.put(item);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
};

// Save multiple items
export const saveItems = async (storeName, items) => {
  const database = await initDB();
  const transaction = database.transaction(storeName, 'readwrite');
  const store = transaction.objectStore(storeName);

  return new Promise((resolve, reject) => {
    items.forEach(item => store.put(item));
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
  });
};

// Get item by key
export const getItem = async (storeName, key) => {
  const store = await getStore(storeName);
  return new Promise((resolve, reject) => {
    const request = store.get(key);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
};

// Get all items from store
export const getAllItems = async (storeName) => {
  const store = await getStore(storeName);
  return new Promise((resolve, reject) => {
    const request = store.getAll();
    request.onsuccess = () => resolve(request.result || []);
    request.onerror = () => reject(request.error);
  });
};

// Delete item
export const deleteItem = async (storeName, key) => {
  const store = await getStore(storeName, 'readwrite');
  return new Promise((resolve, reject) => {
    const request = store.delete(key);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
};

// Clear store
export const clearStore = async (storeName) => {
  const store = await getStore(storeName, 'readwrite');
  return new Promise((resolve, reject) => {
    const request = store.clear();
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
};

// Specific functions for offline queue
export const addToOfflineQueue = async (action) => {
  const queueItem = {
    ...action,
    timestamp: new Date().toISOString(),
    retries: 0
  };
  return saveItem(STORES.OFFLINE_QUEUE, queueItem);
};

export const getOfflineQueue = async () => {
  return getAllItems(STORES.OFFLINE_QUEUE);
};

export const removeFromOfflineQueue = async (id) => {
  return deleteItem(STORES.OFFLINE_QUEUE, id);
};

export const clearOfflineQueue = async () => {
  return clearStore(STORES.OFFLINE_QUEUE);
};

// Specific functions for products
export const saveProducts = async (products) => {
  return saveItems(STORES.PRODUCTS, products);
};

export const getProducts = async () => {
  return getAllItems(STORES.PRODUCTS);
};

// Specific functions for categories
export const saveCategories = async (categories) => {
  return saveItems(STORES.CATEGORIES, categories);
};

export const getCategories = async () => {
  return getAllItems(STORES.CATEGORIES);
};

// Specific functions for tables
export const saveTables = async (tables) => {
  return saveItems(STORES.TABLES, tables);
};

export const getTables = async () => {
  return getAllItems(STORES.TABLES);
};

// Specific functions for orders
export const saveOfflineOrder = async (order) => {
  const offlineOrder = {
    ...order,
    id: `offline_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    synced: false,
    createdOffline: true,
    createdAt: new Date().toISOString()
  };
  await saveItem(STORES.ORDERS, offlineOrder);
  return offlineOrder;
};

export const getOfflineOrders = async () => {
  const orders = await getAllItems(STORES.ORDERS);
  return orders.filter(o => !o.synced);
};

export const markOrderSynced = async (offlineId, serverOrder) => {
  await deleteItem(STORES.ORDERS, offlineId);
  // Optionally save the server order
  if (serverOrder) {
    await saveItem(STORES.ORDERS, { ...serverOrder, synced: true });
  }
};

// Settings
export const saveSetting = async (key, value) => {
  return saveItem(STORES.SETTINGS, { key, value });
};

export const getSetting = async (key) => {
  const item = await getItem(STORES.SETTINGS, key);
  return item?.value;
};

// ==================== IMAGE CACHING FUNCTIONS ====================

/**
 * Convert image URL to base64 data URL
 */
const urlToBase64 = async (url) => {
  try {
    const response = await fetch(url, { mode: 'cors' });
    const blob = await response.blob();
    
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error('[OfflineDB] Error converting image to base64:', url, error);
    return null;
  }
};

/**
 * Cache a single image
 */
export const cacheImage = async (url, type = 'product') => {
  if (!url || url.startsWith('data:')) return url; // Already base64 or empty
  
  try {
    // Check if already cached
    const cached = await getItem(STORES.IMAGES, url);
    if (cached && cached.data) {
      return cached.data;
    }
    
    // Download and convert to base64
    const base64Data = await urlToBase64(url);
    if (!base64Data) return url;
    
    // Save to IndexedDB
    await saveItem(STORES.IMAGES, {
      url,
      data: base64Data,
      type,
      cached_at: new Date().toISOString()
    });
    
    console.info('[OfflineDB] Image cached:', url);
    return base64Data;
  } catch (error) {
    console.error('[OfflineDB] Error caching image:', error);
    return url;
  }
};

/**
 * Get cached image by URL
 */
export const getCachedImage = async (url) => {
  if (!url) return null;
  
  try {
    const cached = await getItem(STORES.IMAGES, url);
    return cached?.data || null;
  } catch (error) {
    console.error('[OfflineDB] Error getting cached image:', error);
    return null;
  }
};

/**
 * Cache all images from products array
 */
export const cacheProductImages = async (products) => {
  const imagesToCache = [];
  
  for (const product of products) {
    if (product.image_url) {
      imagesToCache.push({ url: product.image_url, type: 'product' });
    }
  }
  
  console.info(`[OfflineDB] Caching ${imagesToCache.length} product images...`);
  
  // Cache images in parallel (max 5 at a time)
  const batchSize = 5;
  for (let i = 0; i < imagesToCache.length; i += batchSize) {
    const batch = imagesToCache.slice(i, i + batchSize);
    await Promise.all(batch.map(img => cacheImage(img.url, img.type)));
  }
  
  console.info('[OfflineDB] Product images cached successfully');
};

/**
 * Cache all images from ingredients array
 */
export const cacheIngredientImages = async (ingredients) => {
  const imagesToCache = [];
  
  for (const ingredient of ingredients) {
    if (ingredient.image_url) {
      imagesToCache.push({ url: ingredient.image_url, type: 'ingredient' });
    }
  }
  
  console.info(`[OfflineDB] Caching ${imagesToCache.length} ingredient images...`);
  
  const batchSize = 5;
  for (let i = 0; i < imagesToCache.length; i += batchSize) {
    const batch = imagesToCache.slice(i, i + batchSize);
    await Promise.all(batch.map(img => cacheImage(img.url, img.type)));
  }
  
  console.info('[OfflineDB] Ingredient images cached successfully');
};

/**
 * Get products with cached images (for offline use)
 */
export const getProductsWithCachedImages = async () => {
  const products = await getProducts();
  
  // Replace image URLs with cached base64 data
  const productsWithImages = await Promise.all(
    products.map(async (product) => {
      if (product.image_url) {
        const cachedImage = await getCachedImage(product.image_url);
        if (cachedImage) {
          return { ...product, image_url: cachedImage };
        }
      }
      return product;
    })
  );
  
  return productsWithImages;
};

/**
 * Clear old cached images (older than specified days)
 */
export const clearOldCachedImages = async (daysOld = 7) => {
  try {
    const store = await getStore(STORES.IMAGES, 'readwrite');
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - daysOld);
    
    const request = store.openCursor();
    
    request.onsuccess = (event) => {
      const cursor = event.target.result;
      if (cursor) {
        const cached_at = new Date(cursor.value.cached_at);
        if (cached_at < cutoffDate) {
          cursor.delete();
          console.info('[OfflineDB] Deleted old cached image:', cursor.value.url);
        }
        cursor.continue();
      }
    };
  } catch (error) {
    console.error('[OfflineDB] Error clearing old cached images:', error);
  }
};

/**
 * Get total size of cached images
 */
export const getCachedImagesStats = async () => {
  try {
    const images = await getAllItems(STORES.IMAGES);
    let totalSize = 0;
    
    for (const img of images) {
      if (img.data) {
        totalSize += img.data.length;
      }
    }
    
    return {
      count: images.length,
      totalSizeBytes: totalSize,
      totalSizeMB: (totalSize / (1024 * 1024)).toFixed(2)
    };
  } catch (error) {
    console.error('[OfflineDB] Error getting image stats:', error);
    return { count: 0, totalSizeBytes: 0, totalSizeMB: '0' };
  }
};

export { STORES };
